'use client';

import {
    FILTER,
    FILTER_VAL,
    SHOP_BY_PREFIX,
    TRACK_TYPE_MULTI_ATTR,
} from '@/components/Analytics/constants';
import { useSearchContext } from '@/services/search/SearchProvider';
import { prependCategoryUrl, updatePageTitle } from '@/services/search/utils';
import {
    BreadcrumbNormalized,
    FacetNormalized,
} from '@/src/components/SearchResultsFacets/types';
import { GRSFacetConfigProperties } from '@/types/contentStack';
import { Link, List, Text } from '@nxt-costco-com/forge-components';
import {
    ColorTextDefault,
    SpaceMd,
    SpaceXs,
} from '@nxt-costco-com/forge-design-tokens';
import {
    Analytics,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';
import { ContentStackEntryDataProps } from '@nxt-costco-com/forge-digital-components';
import { ChevronLeft } from '@nxt-costco-com/forge-icons';

import { generateButtonId } from './categories.view';
import { DEFAULT_ITEMS_TO_LIST } from './constants';

export function BreadCrumbFilterView({
    showMoreText,
    facet,
    breadcrumb,
    categoryUrlPath,
    publishedCategories,
    parentCategoryName,
    enabled,
    grsFacetConfig,
    facetConfig,
}: {
    showMoreText: string;
    facet: FacetNormalized;
    breadcrumb?: BreadcrumbNormalized;
    categoryUrlPath?: string;
    publishedCategories: string[];
    parentCategoryName: string;
    enabled: boolean;
    grsFacetConfig?:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    facetConfig?: ContentStackEntryDataProps | undefined;
}) {
    const { queryParams } = useQueryParams();
    const { searchService, searchResult } = useSearchContext();

    if (!enabled || !breadcrumb?.length) {
        return null;
    }

    return (
        <List
            variant="ExpandableList"
            showMoreText={showMoreText}
            ariaLabeledById={facet.facetKey}
            buttonId={generateButtonId(facet)}
            defaultItemsToExpose={DEFAULT_ITEMS_TO_LIST}
        >
            {breadcrumb?.map((category, index) => {
                // last category in breadcrumb chain will be the current category page
                const currentCategory = index === breadcrumb.length - 1;

                if (currentCategory) {
                    updatePageTitle({
                        searchParams: queryParams,
                        grsFacetConfig,
                        searchResult,
                        searchService,
                        pageTitle: category?.name,
                        facetConfig,
                    });

                    return (
                        <Text
                            variant="t5"
                            sx={{
                                color: ColorTextDefault,
                                paddingLeft: '26px',
                            }}
                            aria-current="page"
                            key={`${category.name}-${index}`}
                        >
                            {category.name}
                        </Text>
                    );
                }

                return (
                    <Analytics
                        analyticData={{
                            trackedData: [
                                {
                                    key: FILTER,
                                    value: SHOP_BY_PREFIX + facet.label,
                                },
                                {
                                    key: FILTER_VAL,
                                    value: category.name,
                                },
                            ],
                            trackType: TRACK_TYPE_MULTI_ATTR,
                        }}
                        key={`${category.name}-${index}-analytics`}
                    >
                        <Link
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: SpaceMd,
                            }}
                            href={prependCategoryUrl(
                                categoryUrlPath,
                                category.url,
                                queryParams.toString(),
                                publishedCategories,
                                true,
                            )}
                            aria-label={`${category.name}, ${parentCategoryName}`}
                        >
                            <ChevronLeft
                                sx={{
                                    paddingRight: SpaceXs,
                                    color: ColorTextDefault,
                                }}
                            />
                            <Text
                                variant="t5"
                                sx={{
                                    textDecoration: 'underline',
                                    color: ColorTextDefault,
                                }}
                                component="span"
                                key={`${category.name}-${index}`}
                            >
                                {category.name}
                            </Text>
                        </Link>
                    </Analytics>
                );
            })}
        </List>
    );
}
